// League name ideas: what the create form's *Suggest one* offers (B-430).
//
// A name is built from a pattern and the words that fit it — *The
// {Overconfident} {Supper Club}*, *{Thursday Night} {Panic}* — or is one of a
// list of whole names written as they are. The words here are the ones every
// fantasy sport shares: the draft, waivers, trades, the bench, the group
// chat, the kitchen table. A sport adds its own to the same lists through
// `SportDefinition.leagueNameWords`, so the core never names a sport.
//
// EVERY WORD HAS TO READ RIGHT IN EVERY PATTERN ITS LIST IS USED IN. That is
// the whole of the quality bar, and it is why the lists are separate even
// where two look alike: *Anonymous* makes *Bench Points Anonymous* and would
// make nonsense of *The Reckless Anonymous*.
//
// KEPT OUT ON PURPOSE. Nothing about money or betting (DIRECTION's no
// gambling); nothing that names a league format (*Dynasty*, *Keeper*, *Best
// Ball*), which a name would seem to promise; nothing gendered (*Gentlemen*,
// *Brotherhood*), since a league is whoever is in it; nothing about alcohol,
// since a family plays too; and no other site's or brand's name.

import type { LeagueNameSlot, LeagueNameWords } from '@fsp/sports-core';

/**
 * The longest idea offered. A league's name is on every screen (the step
 * says so), and on a phone a header has room for about this much before it
 * is cut short. A person may still type up to the form's 40.
 */
export const LEAGUE_NAME_IDEA_MAX = 32;

type Words = Readonly<Record<LeagueNameSlot, readonly string[]>>;

/** The words every fantasy sport shares. Exported for the test. */
export const CORE_LEAGUE_NAME_WORDS: Words = {
  names: [
    'The Basement League',
    'Waiver Wire Warriors',
    'Bench Points Anonymous',
    'The Commish Is Always Right',
    'Trust the Process',
    'Twelve Angry Managers',
    'Analysis Paralysis',
    'Paper Champions',
    'Overthinkers Anonymous',
    'Armchair Experts',
    'Couch Coaches',
    'Hindsight Is 20/20',
    'Wait Till Next Year',
    'A League of Our Own',
    'Friends Until the Draft',
    'Frenemies United',
    'Highly Competitive Friendship',
    'The Trophy Chasers',
    'Close but No Trophy',
    'The Thrill of Victory',
    'The Agony of Defeat',
    'Bragging Rights',
    'Sore Losers Club',
    'Gracious Winners Society',
    'The Group Chat',
    'Victory Lap',
    'The Tinkerers',
    'Unpaid Analysts',
    'Spreadsheet Heroes',
    'The Trade Block',
    'Respect the Draft Board',
    'What Could Have Been',
    'The Projections Lied',
    'Benched and Confused',
    'Draft Punk',
    'Ctrl Alt Defeat',
    'Bench Warmers United',
    'The Undrafted',
    'Points Happen',
    'Win Some, Lose More',
    'Trash Talk Central',
    'Champions of Something',
    'The Mild Rivalry',
  ],
  describing: [
    'Overconfident',
    'Questionable',
    'Doubtful',
    'Undrafted',
    'Late-Round',
    'Delusional',
    'Mildly Competitive',
    'Extremely Online',
    'Semi-Pro',
    'Washed-Up',
    'Legendary',
    'Infamous',
    'Notorious',
    'Glorious',
    'Honorable',
    'Unbothered',
    'Caffeinated',
    'Sleep-Deprived',
    'Fearless',
    'Reckless',
    'Hopeless',
    'Ruthless',
    'Relentless',
    'Stubborn',
    'Suspicious',
    'Over-Prepared',
    'Self-Appointed',
    'Highly Decorated',
    'Mostly Harmless',
    'Very Serious',
    'Distinguished',
    'Esteemed',
    'Prestigious',
    'Illustrious',
    'Fabled',
    'Mysterious',
    'Unsupervised',
    'Chaotic',
    'Accidental',
    'Reluctant',
    'Rookie',
    'Veteran',
    'Trash-Talking',
    'Number-Crunching',
    'Spreadsheet',
    'Last-Minute',
    'Overthinking',
    'Second-Guessing',
    'Benchwarming',
    'Tinkering',
    'Lovable',
    'Bewildered',
    'Hopeful',
    'Superstitious',
    'Sophisticated',
    'Unpaid',
    'Honorary',
    'Retired',
    'Undefeated',
    'Winless',
  ],
  gatherings: [
    'Society',
    'Syndicate',
    'Collective',
    'Fellowship',
    'Alliance',
    'Assembly',
    'Council',
    'Federation',
    'Union',
    'Guild',
    'Order',
    'Club',
    'Coalition',
    'Committee',
    'Association',
    'Commission',
    'Consortium',
    'Circle',
    'Summit',
    'Tribunal',
    'Congress',
    'Cabinet',
    'Academy',
    'Institute',
    'Supper Club',
    'Book Club',
    'Support Group',
    'Social Club',
    'Think Tank',
    'Senate',
    'Parliament',
    'Round Table',
    'Brain Trust',
    'Debate Team',
    'Booster Club',
    'Fan Club',
    'Neighborhood Watch',
    'Knitting Circle',
    'Bowling League',
    'Carpool',
    'Brunch Club',
    'Choir',
    'Marching Band',
    'Garden Club',
    'Chess Club',
    'Glee Club',
    'Jury',
  ],
  subjects: [
    'Waiver Wire',
    'Bench Points',
    'Trade Deadline',
    'Draft Day',
    'Mock Draft',
    'Late Round',
    'Last Place',
    'Free Agent',
    'Injury Report',
    'Box Score',
    'Stat Correction',
    'Playoff Bubble',
    'Trade Offer',
    'Lineup Lock',
    'Draft Board',
    'Waiver Claim',
    'Roster Spot',
    'Points Against',
    'Tiebreaker',
    'Trash Talk',
    'Group Chat',
    'Bench Warmer',
    'Draft Pick',
    'Rebuild',
    'Comeback',
    'Bad Trade',
    'Moral Victory',
    'Hot Take',
    'Waiver Priority',
    'Commissioner',
  ],
  societies: [
    'Anonymous',
    'Appreciation Society',
    'Historical Society',
    'Support Group',
    'Survivors',
    'Enthusiasts',
    'Book Club',
    'Think Tank',
    'Brain Trust',
    'Debate Club',
    'Hall of Fame',
    'Fan Club',
    'Academy',
    'Institute',
    'Fellowship',
    'Collective',
    'Committee',
    'Task Force',
    'Research Group',
    'Witness Protection',
    'Recovery Group',
    'Alumni',
    'Social Club',
    'Society',
    'Club',
    'Hotline',
    'Help Desk',
    'Museum',
    'Conspiracy',
    'Experts',
    'Legends',
    'Royalty',
  ],
  moments: [
    'Draft Night',
    'Draft Day',
    'Trade Deadline',
    'Waiver Night',
    'Lineup Lock',
    'Playoff',
    'Championship Week',
    'Rivalry Week',
    'Midseason',
    'Preseason',
    'Opening Day',
    'Mock Draft',
    'Late Night',
    'Weekend',
    'Deadline Day',
    'Waiver Wire',
  ],
  feelings: [
    'Regret',
    'Remorse',
    'Heartbreak',
    'Euphoria',
    'Chaos',
    'Panic',
    'Mayhem',
    'Delusions',
    'Dreams',
    'Despair',
    'Optimism',
    'Hysteria',
    'Glory',
    'Grudges',
    'Therapy',
    'Madness',
    'Meltdown',
    'Bliss',
    'Shenanigans',
    'Nonsense',
    'Fever',
    'Jitters',
    'Magic',
    'Miracles',
    'Mischief',
    'Drama',
    'Fireworks',
    'Heroics',
    'Superstitions',
    'Agony',
    'Confessions',
    'Theater',
    'Frenzy',
    'Fiasco',
    'Hijinks',
    'Rumors',
    'Legends',
  ],
  hangouts: [
    'Basement',
    'Backyard',
    'Couch',
    'Group Chat',
    'Kitchen Table',
    'Garage',
    'Sofa',
    'Break Room',
    'Cul-de-Sac',
    'Office',
    'Living Room',
    'Barbecue',
    'Water Cooler',
    'Group Text',
    'Family Reunion',
    'Carpool',
    'Thanksgiving',
    'Leftovers',
    'Snack Table',
    'Nacho',
    'Wing Night',
    'Grill',
    'Couch Cushion',
    'Recliner',
    'Rec Room',
    'Porch',
    'Patio',
    'Driveway',
    'Lunch Table',
    'Coffee Pot',
    'Spreadsheet',
    'Remote Control',
    'Potluck',
    'Home Office',
    'Block Party',
    'Pizza Night',
    'Game Night',
    'Breakfast Nook',
    'Cookout',
    'Bonfire',
    'Campfire',
    'Treehouse',
    'Attic',
  ],
  events: [
    'Cup',
    'Classic',
    'Invitational',
    'Open',
    'Bowl',
    'Series',
    'Showdown',
    'Championship',
    'Derby',
    'Throwdown',
    'Shootout',
    'Jamboree',
    'Gauntlet',
    'Challenge',
    'Clash',
    'Brawl',
    'Rumble',
    'Melee',
    'Grand Prix',
    'Royale',
    'Games',
    'Trophy',
    'Tournament',
    'Bonanza',
    'Spectacular',
    'Extravaganza',
    'Fiesta',
  ],
  collections: [
    'Moral Victories',
    'Hot Takes',
    'Bold Predictions',
    'Questionable Trades',
    'Lost Causes',
    'Second Guesses',
    'Close Calls',
    'Near Misses',
    'Lineup Regrets',
    'Bench Disasters',
    'Draft-Day Steals',
    'Empty Promises',
    'Last-Minute Swaps',
    'Wasted Picks',
    'Bad Decisions',
    'Great Expectations',
    'Small Victories',
    'Big Dreams',
    'Strong Opinions',
    'Unpopular Opinions',
    'Armchair Experts',
    'Couch Coaches',
    'Benchwarmers',
    'Reluctant Champions',
    'Former Champions',
    'Future Champions',
    'Lovable Losers',
    'Chronic Overthinkers',
    'Ordinary Managers',
    'Friendly Rivals',
    'Sore Losers',
    'Gracious Winners',
    'Trade Vetoes',
    'Waiver Wars',
    'Spreadsheet Warriors',
    'Unpaid Analysts',
    'Questionable Calls',
  ],
};

/**
 * The patterns, each with how often it is picked. Whole names are picked
 * most, since each was written to be a good one; the combinations make the
 * variety.
 */
const PATTERNS: readonly { weight: number; build: (w: Words) => readonly string[] }[] = [
  { weight: 4, build: (w) => w.names },
  { weight: 3, build: (w) => joined(w.describing, w.gatherings, (a, b) => `The ${a} ${b}`) },
  { weight: 3, build: (w) => joined(w.subjects, w.societies, (a, b) => `${a} ${b}`) },
  { weight: 3, build: (w) => joined(w.moments, w.feelings, (a, b) => `${a} ${b}`) },
  { weight: 3, build: (w) => joined(w.hangouts, w.events, (a, b) => `The ${a} ${b}`) },
  // Fewest to choose from, so picked least, or the same few would keep coming up.
  { weight: 1, build: (w) => w.collections.map((c) => `The League of ${c}`) },
];

function joined(
  firsts: readonly string[],
  seconds: readonly string[],
  make: (a: string, b: string) => string,
): string[] {
  return firsts.flatMap((a) => seconds.map((b) => make(a, b)));
}

/** Words a name may share with the one before it: they carry no meaning of their own. */
const LITTLE = new Set(['the', 'of', 'and', 'a', 'an', 'is', 'but', 'no', 'till', 'until']);

/**
 * The words that make a name, lowercased, a plural's `s` dropped, so *Game
 * Night Games* is seen as saying one word twice.
 */
function wordsOf(name: string): string[] {
  return name
    .toLowerCase()
    .split(/[\s-]+/)
    .map((w) => w.replace(/[^a-z0-9/]/g, ''))
    .filter((w) => w.length > 0 && !LITTLE.has(w))
    .map((w) => (w.length > 3 ? w.replace(/s$/, '') : w));
}

/** Fits on a header and says no word twice. */
function reads(name: string): boolean {
  if (name.length > LEAGUE_NAME_IDEA_MAX) return false;
  const words = wordsOf(name);
  return new Set(words).size === words.length;
}

/** The core's words with the sport's added, each list without repeats. */
function merged(sport: LeagueNameWords | undefined): Words {
  const out = {} as Record<LeagueNameSlot, readonly string[]>;
  for (const slot of Object.keys(CORE_LEAGUE_NAME_WORDS) as LeagueNameSlot[]) {
    out[slot] = [...new Set([...CORE_LEAGUE_NAME_WORDS[slot], ...(sport?.[slot] ?? [])])];
  }
  return out;
}

/** Every name each pattern can make for a sport, filtered, worked out once per sport. */
const built = new WeakMap<object, readonly (readonly string[])[]>();
const NO_SPORT = {};

function namesByPattern(sport: LeagueNameWords | undefined): readonly (readonly string[])[] {
  const key = sport ?? NO_SPORT;
  const cached = built.get(key);
  if (cached) return cached;
  const words = merged(sport);
  const lists = PATTERNS.map((p) => [...new Set(p.build(words).filter(reads))]);
  built.set(key, lists);
  return lists;
}

/**
 * Every name the ideas can be, for a sport's words. For the tests and for
 * anyone wondering how many there are; the form never lists them.
 */
export function allLeagueNameIdeas(sport?: LeagueNameWords): string[] {
  return [...new Set(namesByPattern(sport).flat())];
}

/**
 * One league name idea.
 *
 * `avoid` is what has been offered already, newest first, and its first is
 * the name in front of the person now (what they typed, or the idea before):
 * the idea is none of them, and it shares no word with that first one, so
 * each press reads as something new rather than *The Reckless Book Club*
 * after *The Reckless Supper Club*. `random` is `Math.random` or a seeded
 * stand-in, a number from 0 up to 1.
 *
 * It only gives up those two wishes, the word first, when there is nothing
 * left that meets them: after thousands of presses, or for a word list too
 * small to have a choice.
 */
export function leagueNameIdea(
  sport: LeagueNameWords | undefined,
  random: () => number,
  avoid: readonly string[] = [],
): string {
  const lists = namesByPattern(sport);
  const offered = new Set(avoid.map((n) => n.trim().toLowerCase()));
  const before = new Set(wordsOf(avoid[0] ?? ''));
  const fresh = (name: string) => !offered.has(name.toLowerCase());
  const apart = (name: string) => wordsOf(name).every((w) => !before.has(w));

  const weights = PATTERNS.map((p, i) => (lists[i]!.length > 0 ? p.weight : 0));
  const total = weights.reduce((a, b) => a + b, 0);
  const pick = (): string => {
    let roll = random() * total;
    let i = 0;
    while (i < weights.length - 1 && roll >= weights[i]!) roll -= weights[i++]!;
    const list = lists[i]!;
    return list[Math.min(list.length - 1, Math.floor(random() * list.length))]!;
  };

  for (let tries = 0; tries < 60; tries++) {
    const name = pick();
    if (fresh(name) && apart(name)) return name;
  }
  // Rarely reached: a long session or a small list. Fresh first, then anything.
  const all = lists.flat();
  return all.find((n) => fresh(n) && apart(n)) ?? all.find(fresh) ?? pick();
}

/**
 * `count` different ideas, each sharing no word with the one before it, for
 * a client that asks for a handful at once (`GET /api/sports/:sport/league-names`).
 */
export function leagueNameIdeas(
  sport: LeagueNameWords | undefined,
  count: number,
  random: () => number,
): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) out.unshift(leagueNameIdea(sport, random, out));
  return out.reverse();
}
